import { defineStore } from 'pinia'
import { authFetch, getErrorMessage } from '../services/http'
import type { Tenant, TenantCommunityType, TenantPlan, TenantStatus } from '../pages/tenants/types'

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL

type Pagination = {
  page: number
  perPage: number
  total: number
}

export const useTenantsStore = defineStore('tenants', {
  state: () => ({
    items: [] as Tenant[],
    pagination: { page: 1, perPage: 20, total: 0 } as Pagination,
    loading: false,
    error: null as string | null,
  }),
  actions: {
    async list(params: {
      page?: number
      pageSize?: number
      search?: string
      plan?: TenantPlan
      status?: TenantStatus
    }) {
      this.loading = true
      this.error = null
      try {
        const query = new URLSearchParams()
        query.set('page', String(params.page ?? this.pagination.page))
        query.set('pageSize', String(params.pageSize ?? this.pagination.perPage))
        if (params.search) query.set('search', params.search)
        if (params.plan) query.set('plan', params.plan)
        if (params.status) query.set('status', params.status)

        const response = await authFetch(`${apiBaseUrl}/tenants?${query.toString()}`)
        if (!response.ok) {
          const message = await getErrorMessage(response, 'Failed to load tenants')
          throw new Error(message)
        }
        const data = await response.json()
        this.items = data.data
        this.pagination = {
          page: data.meta.page,
          perPage: data.meta.pageSize,
          total: data.meta.total,
        }
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to load tenants'
        throw error
      } finally {
        this.loading = false
      }
    },
    async create(payload: {
      code: string
      name: string
      plan?: TenantPlan
      communityType?: TenantCommunityType
      adminEmail: string
      adminUsername?: string
      adminPassword: string
    }) {
      const response = await authFetch(`${apiBaseUrl}/tenants`, {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to create tenant')
        throw new Error(message)
      }
      return response.json()
    },
    async update(id: string, payload: { name?: string; plan?: TenantPlan; status?: TenantStatus }) {
      const response = await authFetch(`${apiBaseUrl}/tenants/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      })
      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to update tenant')
        throw new Error(message)
      }
      const updated = (await response.json()) as Tenant
      const index = this.items.findIndex((item) => item.id === id)
      if (index !== -1) {
        this.items.splice(index, 1, updated)
      }
      return updated
    },
    async setFeaturesUnlocked(id: string, unlocked: boolean) {
      const response = await authFetch(`${apiBaseUrl}/tenants/${id}/features-unlock`, {
        method: 'PATCH',
        body: JSON.stringify({ unlocked }),
      })
      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to update features-unlocked state')
        throw new Error(message)
      }
      const updated = (await response.json()) as Tenant
      const index = this.items.findIndex((item) => item.id === id)
      if (index !== -1) {
        this.items.splice(index, 1, updated)
      }
      return updated
    },
    async setPilot(id: string, isPilot: boolean) {
      const response = await authFetch(`${apiBaseUrl}/tenants/${id}/pilot`, {
        method: 'PATCH',
        body: JSON.stringify({ isPilot }),
      })
      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to update pilot state')
        throw new Error(message)
      }
      const updated = (await response.json()) as Tenant
      const index = this.items.findIndex((item) => item.id === id)
      if (index !== -1) this.items.splice(index, 1, updated)
      return updated
    },
  },
})
